/**
 * Tool-block carry helpers — preserve live tool results + subagent stream
 * decorations across a MESSAGE_SYNC whose DB write is still in flight.
 *
 * Extracted verbatim from handleMessageSync (syncHandlers.ts) so the
 * RENDER_FROM_TRUTH adoption path can share the exact same carry semantics.
 * Pure: operates only on blocks passed in.
 */

import type { ContentBlock, ToolUseBlock } from "../types";

export type ToolCarry = {
  result?: unknown;
  userAnswers?: Record<string, string>;
  status?: string;
  // Subagent decorations
  subagentType?: string;
  subagentName?: string;
  subagentIcon?: string;
  subagentColor?: string;
  subagentStatus?: ToolUseBlock["subagentStatus"];
  subagentStopReason?: string;
  subagentText?: string;
  subagentTools?: ToolUseBlock["subagentTools"];
  subagentContent?: ToolUseBlock["subagentContent"];
  subagentProgress?: ToolUseBlock["subagentProgress"];
  subagentSummary?: string;
  subagentOutput?: string;
};

export const carryFromBlock = (tb: ToolUseBlock): ToolCarry | null => {
  const hasResult = tb.result !== undefined && tb.result !== null;
  const hasAnswers = !!tb.userAnswers;
  const hasSubagent = !!(
    tb.subagentName ||
    tb.subagentStatus ||
    tb.subagentText ||
    (tb.subagentTools && tb.subagentTools.length) ||
    (tb.subagentContent && tb.subagentContent.length) ||
    tb.subagentProgress ||
    tb.subagentOutput ||
    tb.subagentSummary
  );
  if (!hasResult && !hasAnswers && !hasSubagent) return null;
  return {
    result: tb.result,
    userAnswers: tb.userAnswers,
    status: tb.status,
    subagentType: tb.subagentType,
    subagentName: tb.subagentName,
    subagentIcon: tb.subagentIcon,
    subagentColor: tb.subagentColor,
    subagentStatus: tb.subagentStatus,
    subagentStopReason: tb.subagentStopReason,
    subagentText: tb.subagentText,
    subagentTools: tb.subagentTools,
    subagentContent: tb.subagentContent,
    subagentProgress: tb.subagentProgress,
    subagentSummary: tb.subagentSummary,
    subagentOutput: tb.subagentOutput,
  };
};

export const applyCarry = (
  block: ContentBlock,
  carry: ToolCarry
): ContentBlock => {
  const tb = block as ToolUseBlock;
  const merged: ToolUseBlock = { ...tb };
  // result/status only fill when missing (don't overwrite a fresher server value)
  if (!tb.result && carry.result !== undefined) {
    merged.result = carry.result;
    if (carry.status) merged.status = carry.status as ToolUseBlock["status"];
  }
  if (carry.userAnswers) merged.userAnswers = carry.userAnswers;
  // Subagent decorations: ALWAYS prefer the richer source (whichever side has them).
  // The carry side already passed the hasSubagent guard above.
  if (carry.subagentType && !tb.subagentType)
    merged.subagentType = carry.subagentType;
  if (carry.subagentName && !tb.subagentName)
    merged.subagentName = carry.subagentName;
  if (carry.subagentIcon && !tb.subagentIcon)
    merged.subagentIcon = carry.subagentIcon;
  if (carry.subagentColor && !tb.subagentColor)
    merged.subagentColor = carry.subagentColor;
  // Status and reason travel as ONE UNIT. Under the old independent `!tb.*` guards they
  // could separate: a block that already said 'running' kept that status while the other
  // side's reason filled in beside it, producing {running, turn_cap} — a live spinner over
  // a helper that had already hit a wall, with the reason there to prove it.
  //
  // MONOTONIC, the same rule both END handlers follow: a SETTLED carry ('stopped' or
  // 'error') outranks whatever this block says, because the block can be a mid-stream
  // snapshot taken before the child finished — which is exactly the server row a local
  // {stopped, turn_cap} lane is merged into. Otherwise the old fill-when-missing stands,
  // and a settled block is never downgraded by a running carry.
  const carrySettled =
    carry.subagentStatus === "stopped" || carry.subagentStatus === "error";
  if (carrySettled || !tb.subagentStatus) {
    if (carry.subagentStatus) merged.subagentStatus = carry.subagentStatus;
    if (carry.subagentStopReason)
      merged.subagentStopReason = carry.subagentStopReason;
  }
  if (carry.subagentText && !tb.subagentText)
    merged.subagentText = carry.subagentText;
  // The two stream arrays keep the LONGER side, not merely the non-empty one.
  // Emptiness stopped being a safe discriminator once the server began persisting
  // these: a sync landing mid-stream carries a materialized lane that is real but
  // SHORTER than what this tab has already accumulated, and filling only-when-empty
  // would hand the base's shorter copy the win and visibly truncate the rail. These
  // streams are append-only, so longer is strictly richer — which is what the
  // comment above always claimed this did.
  if (
    carry.subagentTools &&
    carry.subagentTools.length > (tb.subagentTools?.length ?? 0)
  ) {
    merged.subagentTools = carry.subagentTools;
  }
  if (
    carry.subagentContent &&
    carry.subagentContent.length > (tb.subagentContent?.length ?? 0)
  ) {
    merged.subagentContent = carry.subagentContent;
  }
  if (carry.subagentProgress && !tb.subagentProgress)
    merged.subagentProgress = carry.subagentProgress;
  if (carry.subagentSummary && !tb.subagentSummary)
    merged.subagentSummary = carry.subagentSummary;
  if (carry.subagentOutput && !tb.subagentOutput)
    merged.subagentOutput = carry.subagentOutput;
  return merged as ContentBlock;
};

/** Build a toolUseId → carry map from every tool_use block in `messages`. */
export function buildToolCarryMap(
  messages: ReadonlyArray<{ content: ContentBlock[] }>
): Map<string, ToolCarry> {
  const map = new Map<string, ToolCarry>();
  for (const msg of messages) {
    for (const block of msg.content) {
      if (block.type !== "tool_use") continue;
      const carry = carryFromBlock(block as ToolUseBlock);
      if (carry) map.set((block as ToolUseBlock).toolUseId, carry);
    }
  }
  return map;
}
